import { getTime } from '@/utils/date';
import { isArray, isEmpty } from '@/utils/is';
import { View, TextLayer, LineRainbowLayer, HeatGridLayer, CircleLayer } from 'mapvgl';
import { stateMapMarker as smm, pointState } from '@/utils/dict';
import { getDeviceTypeImg } from '@/utils/deviceUtils/index';
import ctt from '@/utils/const';
import gcoord from 'gcoord';
import { genComponentEl } from './utils';
import { clamp, fixedNumber } from './math';

export function makeView(bmap) {
  return new View({
    map: bmap
  });
}

export function makeHeatGridLayer({ view, options }) {
  const layer = new HeatGridLayer(
    options || {
      max: 100, // 最大阈值
      min: 0, // 最小阈值
      // color: function() {
      //     return 'rgb(200, 255, 0)';
      // },
      gridSize: 12,
      style: 'normal',
      gradient: {
        // 对应比例渐变色
        0: 'rgba(0,0,128,1)',
        0.125: 'rgba(0,4,255,1)',
        0.376: 'rgb(2,255,255)',
        0.62: 'rgba(254,255,1,1)',
        0.87: 'rgba(255,0,0,1)',
        '1.0': 'rgba(130,0,0,1)'
      },
      // textOptions: {
      //     show: true,
      //     color: '#f00'
      // },
      riseTime: 1000, // 楼块初始化升起时间
      maxHeight: 200, // 最大高度
      minHeight: 10 // 最小高度
    }
  );
  joinToView(view, layer);
  return layer;
}

function joinToView(view, layer) {
  if (!view) return;
  if (view) view.addLayer(layer);
}

export function makeLineRainbowLayer(view) {
  const lineLayer = new LineRainbowLayer({
    style: 'road', // road, arrow, normal
    width: 5,
    color: ['#f00', '#f0f', '#0af', '#0a0', '#F09', '#a09', '#ff0', '#fd0', '#f90', '#f00']
  });
  joinToView(view, lineLayer);
  return lineLayer;
}

/**
 * 数据转Point GeoJson
 * @param {[number, number]} coordinates [lngitude, latitude]
 */
export function toPointGeoJson(coordinates, count) {
  return {
    geometry: {
      type: 'Point',
      coordinates: coordinates
    },
    properties: {
      count
    }
  };
}

/**
 * 数据转LineString GeoJson
 * @param {Array<[number, number]>} coordinates [lngitude, latitude][]
 */
export function toLineStringGeoJson(coordinates) {
  return [
    {
      geometry: {
        type: 'LineString',
        coordinates
      }
    }
  ];
}

export function to(bmap, { center, zoom, tilt, heading }, animationOpts = {}) {
  const { lng, lat } = bmap.getCenter();
  const _zoom = bmap.getZoom();
  const _tilt = bmap.getTilt();
  zoom = zoom || 18;
  tilt = tilt || 0;
  heading = heading || 0;

  const keyFrames = [
    {
      center: new BMapGL.Point(lng, lat),
      zoom: _zoom,
      tilt: _tilt,
      heading: 0,
      percentage: 0
    },
    {
      center: new BMapGL.Point(...center),
      zoom,
      tilt,
      heading,
      percentage: 0.8
    },
    {
      center: new BMapGL.Point(...center),
      zoom,
      tilt,
      heading,
      percentage: 1
    }
  ];
  const opts = {
    delay: 0,
    duration: 1500,
    interation: 1,
    ...animationOpts
  };

  const animation = new BMapGL.ViewAnimation(keyFrames, opts);
  bmap.startViewAnimation(animation);
  return () => bmap.cancelViewAnimation(animation);
}

export function toCoordinate(position) {
  const def = [116.418261, 39.921984];
  if (isArray(position)) {
    if (!position[0] && !position[1]) {
      return def;
    } else {
      return position;
    }
  }
  if (typeof position === 'string') return position.split(' ');
  return def;
}

// 二分查找
export function findIndexByRange(nums, [min, max]) {
  let left = 0;
  let right = nums.length - 1;
  let mid = -1;
  while (left <= right) {
    mid = Math.floor((right - left) / 2) + left;
    const num = nums[mid];
    if (num > min && num < max) {
      return mid;
    } else if (num > max) {
      right = mid - 1;
    } else {
      left = mid + 1;
    }
  }
  return right;
}

export function findIndexByTime(nums, time) {
  return nums.findIndex(e => time < e);
}

export function getRange(time, speed) {
  let multiple = 0.5 * speed;
  multiple = multiple < 1.5 ? 1.5 : multiple;
  let boundary = getTime('s') * multiple;
  return [time - boundary, time];
}

/**
 * 百度地图坐标转换
 * @param {{from?: typeof gcoord.CRSTypes, to?: typeof gcoord.CRSTypes, coordinates: [number, number][]}} options
 * @returns {Promise}
 */
export async function toBmapCoordinate(options) {
  const { coordinates, from, to } = {
    from: gcoord.WGS84,
    to: gcoord.BMap,
    ...options
  };
  const res = coordinates.map(e => {
    return gcoord.transform(e, from, to);
  });
  return res;
}

/**
 * 将百度地图坐标转换为WGS84格式坐标
 * @param {[number, number]} coordinate 百度地图坐标 [lng, lat]
 * @returns {[number, number]} WGS84格式坐标 [lng, lat]
 */
export function bmap2Wgs84(coordinate) {
  return gcoord.transform(coordinate, gcoord.BMap, gcoord.WGS84);
}

/**
 * 验证坐标是否有效
 * @param {[number, number]} coordinate 坐标 [lng, lat]
 * @returns {boolean} 坐标是否有效
 */
export function isValidCoordinate(coordinate) {
  if (!Array.isArray(coordinate) || coordinate.length !== 2) {
    return false;
  }

  const [lng, lat] = coordinate;

  // 检查是否为有效数字
  if (typeof lng !== 'number' || typeof lat !== 'number') {
    return false;
  }

  if (isNaN(lng) || isNaN(lat)) {
    return false;
  }

  // 验证经度范围：-180 到 180
  if (lng < -180 || lng > 180) {
    return false;
  }

  // 验证纬度范围：-90 到 90
  if (lat < -90 || lat > 90) {
    return false;
  }

  return true;
}

/**
 * 将百度地图坐标转换为 CGCS2000 格式坐标
 * @param {[number, number]} coordinate 百度地图坐标 [lng, lat]
 * @returns {[number, number]} CGCS2000 格式坐标 [lng, lat]
 * @throws {Error} 当输入坐标无效时抛出错误
 */
export function bmap2CGCS2000(coordinate) {
  // 验证输入坐标的有效性
  if (!isValidCoordinate(coordinate)) {
    throw new Error(
      `无效的坐标值：${JSON.stringify(
        coordinate
      )}。经度范围应为 [-180, 180]，纬度范围应为 [-90, 90]`
    );
  }

  // 先将百度坐标转换为 WGS84
  const wgs84 = gcoord.transform(coordinate, gcoord.BMap, gcoord.WGS84);

  // 验证转换后的坐标有效性
  if (!isValidCoordinate(wgs84)) {
    throw new Error(`坐标转换后超出有效范围：${JSON.stringify(wgs84)}`);
  }

  // CGCS2000 与 WGS84 椭球参数极为接近，精度差异在毫米级，可直接使用 WGS84 坐标作为 CGCS2000 坐标
  return wgs84;
}

/**
 * 将GPRMC格式坐标转换为WGS84格式坐标
 *
 * @param {number[]} coord GPRMC格式坐标数组
 * @returns {number[]} WGS84格式坐标数组
 */
export function gprmc2Wgs84(coord) {
  const parser = v => {
    let pointIndex = v.indexOf('.');
    if (pointIndex == -1) return 0;

    let h = v.substr(0, pointIndex - 2);

    if (h.indexOf('-') !== -1) {
      return Number(h) - Number(v.substring(pointIndex - 2)) / 60;
    } else {
      return Number(h) + Number(v.substring(pointIndex - 2)) / 60;
    }
  };

  return coord.map(e => {
    return parser(`${e}`);
  });
}

/**
 * @param {{from?:typeof gcoord.CRSTypes, to?: typeof gcoord.CRSTypes,, coordinate: [number,number]}} options
 */
export async function toBmapPos(options) {
  const { coordinate, from, to } = {
    from: gcoord.WGS84,
    to: gcoord.BMap,
    ...options
  };

  return gcoord.transform(coordinate, from, to);
}

/**
 * @param {{from?:typeof gcoord.CRSTypes, to?: typeof gcoord.CRSTypes,, coordinate: [number,number]}} options
 */
export function toBmapPos2(options) {
  const { coordinate, from, to } = {
    from: gcoord.WGS84,
    to: gcoord.BMap,
    ...options
  };

  if (isEmpty(coordinate[0])) coordinate[0] = 0;
  if (isEmpty(coordinate[1])) coordinate[1] = 0;

  if (coordinate[0] === 0 && coordinate[1] === 0) return [0, 0];

  const coord = gcoord.transform(coordinate, from, to);

  return [fixedNumber(coord[0], 8), fixedNumber(coord[1], 8)];
}

export function makeDeviceMarker({ state, coordinate }) {
  window.BMapGL = window.BMapGL || window.BMap;

  const url = smm[state];
  const icon = new BMapGL.Icon(url, new BMapGL.Size(32, 32));
  // 创建Marker标注，使用小车图标
  const pt = new BMapGL.Point(...coordinate);
  const marker = new BMapGL.Marker(pt, { icon });
  return marker;
}

export function makeBmapV3DeviceMarker({ state, coordinate }) {
  const url = smm[state];
  const icon = new BMap.Icon(url, new BMap.Size(32, 32));
  // 创建Marker标注，使用小车图标
  const pt = new BMap.Point(...coordinate);
  const marker = new BMap.Marker(pt, { icon });
  return marker;
}

export function patchDeviceMarker({ marker, state, coordinate }) {
  if (!marker) return false;
  const [lng, lat] = coordinate;
  const m = {
    icon: marker.getIcon().imageUrl,
    ...marker.getPosition()
  };
  if (m.icon !== smm[state]) {
    marker.setIcon(new BMapGL.Icon(smm[state], new BMapGL.Size(32, 32)));
  }
  if ((lng !== 0 || lat !== 0) && (m.lng !== lng || m.lat !== lat)) {
    marker.setPosition(new BMapGL.Point(...coordinate));
  }
}

export const colorMap = {
  undefined: '#a9a9a9',
  '-1': '#a9a9a9', // unconnect-color 未连接
  0: '#03b36e', // success-color 成功
  1: '#ff6100', // warning-color 预警
  2: '#ff4847', // danger-color 报警
  61: '#ffd04f', // fix-color 维护
  71: '#ffbb00', // fault-color 故障
  81: '#00a1ff' // collect-color 采集
};

export const colors = {
  undefined: 'unconnect-color',
  '-1': 'unconnect-color',
  0: 'success-color',
  1: 'warning-color',
  2: 'danger-color',
  61: 'fix-color',
  71: 'fault-color',
  81: 'collect-color'
};

/**
 * 初始化多个marker
 * @param {{source: Array<any>, markerMap: {[string]: any}, bmap: BMapGL, clickCallback: (e) => void }} param0
 */
export function initMarker({ source, markerMap, bmap, clickCallback }) {
  const devices = source;
  const map = markerMap;
  let marker;
  devices.forEach(e => {
    if (!e.longitude || !e.latitude) return;
    marker = map[e.sn] = makeDeviceMarker({
      state: e.state,
      coordinate: [e.longitude, e.latitude]
    });
    bmap.addOverlay(marker);
    marker.addEventListener('click', () => {
      clickCallback(e);
    });
  });
}

export function setLabel({ sn, coordinate, content, bmap, labelMap }) {
  let label = labelMap[sn];

  if (!label) {
    const point = new BMapGL.Point(...coordinate);
    label = new BMapGL.Label(content, {
      // 创建文本标注
      position: point,
      offset: new BMapGL.Size(0, -55)
    });
    labelMap[sn] = label;
    bmap.addOverlay(label); // 将标注添加到地图中
    label.setStyle({
      // 设置label的样式
      color: '#000',
      fontSize: '16px',
      border: '2px solid #1E90FF',
      transform: 'translateX(-50%)',
      borderRadius: '8px',
      padding: '2px 5px'
    });
  } else {
    label.setContent(content);
  }
}

export function infoContent({
  deviceIdMap,
  colors,
  name,
  parentId,
  state,
  sn,
  deviceType,
  router,
  multiMonitorCallback
}) {
  let officeName = (deviceIdMap[parentId] || {}).name || '----';
  const pic = getDeviceTypeImg(deviceType).picture;

  return genComponentEl({
    template: `
    <div class="custom-info-content">
      <div class="info-wrap">
        <div class="left">
          <div>
          <el-image
            style="width: 100px;height:110px;"
            src="${pic}"
            fit="scale-down"
            :preview-src-list="['${pic}']">
          </el-image>
          </div>
        </div>
        <div class="right">
          <div>
            <span>{{'设备名:' | translateTo(i18nPath)}}</span>
            <span>${name}</span>
          </div>
          <div>
            <span>{{'机构名称:' | translateTo(i18nPath)}}</span>
            <span>${officeName}</span>
          </div>
          <div>
            <span>{{'状态:' | translateTo(i18nPath)}}</span>
            <span class="state-text ${colors[state]}">
              {{'${pointState[state]}' | translateTo('device.state')}}
            </span>
          </div>
        </div>
      </div>
      <div class="controls">
        <el-button
          v-if="hasMuiltiMonitor"
          type="text"
          size="mini"
          @click="multiMonitorCallback"
          :disabled="${state === ctt.POINT_STATE_UNCONNECT} && (${deviceType !== ctt.GW1041} && ${
      deviceType !== ctt.BU_10
    })"
        >
          {{ $t('多屏监控') }}
        </el-button>
        <el-button
          v-if="authMenu['01020201'] && [${ctt.GD_606}, ${ctt.GD_612}].includes(deviceType)"
          type="text"
          size="mini"
          @click="toTrackPage"
          :disabled="${state === ctt.POINT_STATE_UNCONNECT}"
        >
          {{ $t('设备轨迹') }}
        </el-button>
        <el-button type="text" size="mini" @click="pushToData"
          :disabled="${state === ctt.POINT_STATE_UNCONNECT} && (${deviceType !== ctt.GW1041} && ${
      deviceType !== ctt.BU_10
    })"
        >
          {{ $t('监控页面') }}
        </el-button>
      </div>
    </div>
    `,

    data() {
      return {
        i18nPath: 'monitor.scene',
        hasMuiltiMonitor: !!multiMonitorCallback
      };
    },
    methods: {
      multiMonitorCallback() {
        multiMonitorCallback?.();
      },
      toTrackPage() {
        router.push({
          name: 'DeviceTrack',
          params: {
            sn,
            deviceType
          }
        });
      },
      pushToData() {
        router.push({
          name: 'Realdata',
          params: {
            sn,
            deviceType
          }
        });
      }
    }
  });
}

export function makeCircleLayer({ options }) {
  const { coordinates } = options;

  const layer = new CircleLayer({
    type: 'wave',
    color: 'rgba(50, 50, 200, 1)',
    size: 10,
    radius: 30,
    data: [
      {
        geometry: {
          type: 'Point',
          coordinates
        }
      }
    ],
    ...options
  });

  return layer;
}

export function makeTextLayer({ options }) {
  const { coordinates, text } = options;

  const layer = new TextLayer({
    color: '#fff',
    fontFamily: 'Microsoft Yahei',
    fontSize: 14,
    flat: false,
    collides: true,
    offset: [0, -20],
    padding: [2, 2],
    margin: [0, 0],
    data: [
      {
        geometry: {
          type: 'Point',
          coordinates
        },
        properties: {
          text
        }
      }
    ],
    ...options
  });

  return layer;
}

export function getCenterFromCoordinates(coordinates) {
  if (!coordinates || coordinates.length === 0) {
    return [0, 0];
  }
  // 计算所有坐标的边界
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  coordinates.forEach(coord => {
    const [lng, lat] = coord;
    if (lng < minX) minX = lng;
    if (lng > maxX) maxX = lng;
    if (lat < minY) minY = lat;
    if (lat > maxY) maxY = lat;
  });

  // 计算中心点
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;

  return [centerX, centerY];
}

/**
 * 通过经纬度列表计算出适合的zoom层级
 * @param {Array<[number, number]>} coordinates 经纬度列表
 * @param {BMapGL} bmap 百度地图实例
 * @returns {number} 适合的zoom层级
 */
export function getZoomFromCoordinates(coordinates, factor = 1) {
  if (!coordinates || coordinates.length === 0) {
    return 18; // 默认zoom层级
  }

  // 计算所有坐标的边界
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  coordinates.forEach(coord => {
    const [lng, lat] = coord;
    if (lng < minX) minX = lng;
    if (lng > maxX) maxX = lng;
    if (lat < minY) minY = lat;
    if (lat > maxY) maxY = lat;
  });

  // 计算适合的zoom层级
  const mapWidth = window.innerWidth;
  const mapHeight = window.innerHeight;

  // 经纬度范围
  const deltaLng = maxX - minX;
  const deltaLat = maxY - minY;

  // 经纬度到像素的转换因子（百度地图）
  const lngPerPixel = deltaLng / mapWidth;
  const latPerPixel = deltaLat / mapHeight;

  // 根据经纬度范围计算zoom层级
  // 这里使用一个简化的计算方式，实际可能需要更复杂的地图投影计算
  // 假设在zoom 18时，1个像素大约对应0.00001度经纬度
  const baseZoom = 18;
  const baseScale = 0.00001 * factor; // 在zoom 18时每像素代表的经纬度差

  // 计算需要的缩放比例
  const requiredLngScale = lngPerPixel / baseScale;
  const requiredLatScale = latPerPixel / baseScale;
  const requiredScale = Math.max(requiredLngScale, requiredLatScale);

  // 计算zoom层级
  const zoom = baseZoom - Math.log2(requiredScale);

  // 确保zoom在有效范围内
  return clamp(parseInt(zoom), 3, 19);
}

export function makeHtmlLayer(createDOM, options) {
  var canvasLayer = new BMapGL.CustomHtmlLayer(createDOM, {
    offsetX: 0,
    offsetY: 0,
    minZoom: 1,
    maxZoom: 24,
    enableDraggingMap: true,
    ...options
  });

  return {
    layer: canvasLayer,
    setData(list) {
      const data = {
        type: 'FeatureCollection',
        features: []
      };

      list.forEach(item => {
        const { coordinate, ...props } = item;

        data.features.push({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: coordinate
          },
          properties: props
        });
      });

      this.layer.setData(data);
    }
  };
}
